import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Academy, Club, Organization } from '@court-sync/shared';
import { hasAcademyManagementRole, hasClubManagementRole, hasRole } from '@court-sync/shared';
import { getUserAcademiesFromCache, fetchUserAcademiesQuery } from '@/hooks/useDataStore';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { useAuthStore } from '@/store/auth';
import { organizationService } from '@/services/organization';
import { clubService } from '@/services/club';

export function useOrganizationSettingsData() {
  const { user } = useAuthStore();
  const { hasClubManagement, isLoading: clubEntitlementsLoading } = useClubModuleAccess();
  const { currentClub, isLoading: clubLoading, setCurrentClub } = useCurrentClub();
  const selectedClubId = currentClub?.id;

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [academy, setAcademy] = useState<Academy | null>(null);
  const [club, setClub] = useState<Club | null>(null);
  const [loading, setLoading] = useState(true);

  const hasAcademyManagement = Boolean(user && hasAcademyManagementRole(user));
  const hasClubRole = Boolean(user && hasClubManagementRole(user));
  const isAcademyOwner = Boolean(user && hasRole(user, 'academy_owner'));
  const canOperateClub = hasClubRole || isAcademyOwner;
  const hasClubModule = canOperateClub && hasClubManagement && !clubEntitlementsLoading;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const orgRes = await organizationService.getMyOrganization();
      if (orgRes.success && orgRes.data) {
        setOrganization(orgRes.data);
      }

      if (hasAcademyManagement) {
        await fetchUserAcademiesQuery({ force: true });
        const academies = getUserAcademiesFromCache();
        setAcademy(academies[0] ?? null);
      } else {
        setAcademy(null);
      }

      if (selectedClubId) {
        const clubRes = await clubService.getClub(selectedClubId);
        if (clubRes.success && clubRes.data) {
          setClub(clubRes.data);
        }
      } else {
        setClub(null);
      }
    } finally {
      setLoading(false);
    }
  }, [hasAcademyManagement, selectedClubId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const subtitle = useMemo(() => {
    const parts: string[] = [];
    if (academy?.name) parts.push(academy.name);
    if (club && !club.isInternalShell && club.name) parts.push(club.name);
    return parts.join(' · ') || organization?.name;
  }, [academy?.name, club, organization?.name]);

  const clubForTabs = club && !club.isInternalShell ? club : null;

  const isInitialLoading =
    loading || clubEntitlementsLoading || (hasClubModule && clubLoading && !club);

  return {
    organization,
    setOrganization,
    academy,
    setAcademy,
    club,
    setClub,
    clubForTabs,
    subtitle,
    isInitialLoading,
    hasAcademyManagement,
    hasClubModule,
    setCurrentClub,
    reload: loadData,
  };
}
